// Issue-tracker CSV parsing for the data import wizard: our own template, or a
// full-field export from another tool. Exports repeat a header once per value
// (Comment, Labels, Sprint, Watchers…), so rows are read as arrays and grouped
// by header name.

/** RFC 4180 CSV -> array of rows (quoted fields may contain commas and newlines). */
export const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  const s = String(text || "").replace(/^﻿/, "");
  for (let i = 0; i < s.length; i += 1) {
    const c = s[i];
    if (quoted) {
      if (c === '"' && s[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && s[i + 1] === "\n") i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else field += c;
  }
  if (field || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((v) => v !== ""));
};

const MONTHS = { jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11 };

/** "24/Sep/26 6:29 PM", "2026-09-24 18:29" or ISO -> ISO string; "" when empty or unreadable. */
export const parseDate = (v) => {
  const s = String(v || "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{1,2})\/([A-Za-z]{3})\/(\d{2,4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AP]M)?)?$/i);
  if (m && MONTHS[m[2].toLowerCase()] !== undefined) {
    let h = Number(m[4] || 0);
    if (m[7]) h = (h % 12) + (m[7].toUpperCase() === "PM" ? 12 : 0);
    const y = Number(m[3]) < 100 ? 2000 + Number(m[3]) : Number(m[3]);
    return new Date(y, MONTHS[m[2].toLowerCase()], Number(m[1]), h, Number(m[5] || 0), Number(m[6] || 0)).toISOString();
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
};

const KEY_RE = /^[A-Z][A-Z0-9]*-\d+$/;

/**
 * @returns {{ issues, links, accounts, projects, warnings }}
 *   issues: normalised issues (+ placeholder parents referenced but not exported)
 */
export const readExport = (text) => {
  const rows = parseCsv(text);
  if (rows.length < 2) throw new Error("The file has no issues");
  const header = rows[0].map((h) => h.trim());
  const cols = new Map();
  header.forEach((h, i) => cols.set(h, [...(cols.get(h) || []), i]));
  if (!cols.has("Summary") || !cols.has("Issue key")) throw new Error('The file needs at least the "Summary" and "Issue key" columns. Download the template to see the expected format.');
  const one = (r, ...names) => {
    for (const n of names) for (const i of cols.get(n) || []) if (String(r[i] ?? "").trim()) return String(r[i]).trim();
    return "";
  };
  const all = (r, name) => (cols.get(name) || []).map((i) => String(r[i] ?? "").trim()).filter(Boolean);
  const linkCols = header.map((h, i) => ({ i, m: h.match(/^(Outward|Inward) issue link \((.+)\)$/) })).filter((x) => x.m);

  const accounts = {};
  const issues = [];
  const links = [];
  for (const r of rows.slice(1)) {
    const key = one(r, "Issue key").toUpperCase();
    if (!key) continue;
    // without a project column, the key prefix (PRJ-12 -> PRJ) names the project
    const projectKey = (one(r, "Project key") || key.replace(/-\d+$/, "")).toUpperCase();
    for (const [n, id] of [["Assignee", "Assignee Id"], ["Reporter", "Reporter Id"], ["Creator", "Creator Id"], ["Project lead", "Project lead id"]]) {
      const name = one(r, n);
      const acc = one(r, id);
      if (name && acc) accounts[acc] = name;
    }
    const parentRaw = one(r, "Parent key") || (KEY_RE.test(one(r, "Parent").toUpperCase()) ? one(r, "Parent") : "") || one(r, "Custom field (Epic Link)", "Epic key");
    const sprints = all(r, "Sprint");
    issues.push({
      key, id: one(r, "Issue id"), summary: one(r, "Summary"), type: one(r, "Issue Type"),
      status: one(r, "Status"), statusCategory: one(r, "Status category"), statusChangedAt: parseDate(one(r, "Status category changed")),
      projectKey, projectName: one(r, "Project name") || projectKey, projectType: one(r, "Project type"),
      priority: one(r, "Priority"), resolution: one(r, "Resolution"),
      assignee: one(r, "Assignee") ? { name: one(r, "Assignee"), id: one(r, "Assignee Id") } : null,
      reporter: one(r, "Reporter") ? { name: one(r, "Reporter"), id: one(r, "Reporter Id") } : null,
      created: parseDate(one(r, "Created")), updated: parseDate(one(r, "Updated")), resolved: parseDate(one(r, "Resolved")),
      dueDate: parseDate(one(r, "Due date")), startDate: parseDate(one(r, "Start date", "Custom field (Start date)")),
      description: one(r, "Description"), environment: one(r, "Environment"),
      labels: [...all(r, "Labels"), ...sprints.map((s) => `sprint-${s}`)],
      sprints,
      storyPoints: one(r, "Story Points", "Custom field (Story Points)", "Custom field (Story point estimate)"),
      originalEstimate: one(r, "Original estimate"), remainingEstimate: one(r, "Remaining Estimate"),
      parentKey: parentRaw.toUpperCase(), parentSummary: one(r, "Parent summary"),
      // "date;author;text" (author = account id or a person's name/email) — the text may contain ';'
      comments: all(r, "Comment").map((c) => {
        const [d, author, ...rest] = c.split(";");
        return rest.length ? { created: parseDate(d), authorId: (author || "").trim(), body: rest.join(";") } : { created: "", authorId: "", body: c };
      }).filter((c) => c.body.trim()),
      // "date;accountId;file name;url"
      attachments: all(r, "Attachment").map((a) => {
        const p = a.split(";");
        return { name: p[2] || p[p.length - 1], url: p.slice(3).join(";") || p[p.length - 1] };
      }),
    });
    for (const { i, m } of linkCols) {
      const other = String(r[i] || "").trim().toUpperCase();
      if (!other) continue;
      links.push(m[1] === "Outward" ? { from: key, to: other, type: m[2] } : { from: other, to: key, type: m[2] });
    }
  }

  // a comment author that is not a known account id is a person's name/email (template files)
  for (const it of issues) for (const c of it.comments) if (c.authorId && !accounts[c.authorId] && !/^[0-9a-f]{6,}[:-]/i.test(c.authorId)) accounts[c.authorId] = c.authorId;

  // parents referenced by the file but not included in it become placeholder issues
  const have = new Set(issues.map((i) => i.key));
  const missing = new Map();
  for (const it of issues) {
    if (!it.parentKey || have.has(it.parentKey)) continue;
    const p = missing.get(it.parentKey) || { children: [], it };
    p.children.push(it);
    missing.set(it.parentKey, p);
  }
  const placeholders = [...missing].map(([key, { children, it }]) => {
    const isSub = children.some((c) => /sub-?task/i.test(c.type));
    const allDone = children.every((c) => /done/i.test(c.statusCategory));
    const anyStarted = children.some((c) => !/to ?do/i.test(c.statusCategory));
    return {
      key, id: "", summary: it.parentSummary || key, type: isSub ? "Task" : "Epic", placeholder: true,
      status: allDone ? "Done" : anyStarted ? "In Progress" : "To Do", statusCategory: allDone ? "Done" : anyStarted ? "In Progress" : "To Do",
      projectKey: it.projectKey, projectName: it.projectName, priority: "", resolution: allDone ? "Done" : "",
      assignee: null, reporter: null, created: children.map((c) => c.created).filter(Boolean).sort()[0] || "", updated: "", resolved: "",
      description: "", labels: [], comments: [], attachments: [], parentKey: "",
    };
  });

  const projects = {};
  for (const it of [...issues, ...placeholders]) {
    const p = projects[it.projectKey] || { key: it.projectKey, name: it.projectName || it.projectKey, type: it.projectType, count: 0 };
    p.count += 1;
    projects[it.projectKey] = p;
  }
  return { issues: [...issues, ...placeholders], placeholders: placeholders.length, links, accounts, projects };
};

// ---------------------------------------------------------------------------
// Auto-mapping imported values onto workspace configuration

const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const TYPE_SYNONYMS = { subtask: "subtask", subtasks: "subtask", epic: "epic", story: "story", userstory: "story", newfeature: "story", improvement: "story", feature: "story", task: "task", bug: "bug", defect: "bug", incident: "bug", problem: "bug" };
const STATUS_SYNONYMS = {
  open: "todo", backlog: "todo", new: "todo", selectedfordevelopment: "todo", todo: "todo", reopened: "todo",
  inprogress: "in_progress", indevelopment: "in_progress", development: "in_progress",
  inreview: "in_review", codereview: "in_review", review: "in_review", peerreview: "in_review",
  qa: "testing", testing: "testing", intesting: "testing", inqa: "testing", readyfortesting: "testing", uat: "testing",
  hold: "waiting", onhold: "waiting", blocked: "waiting", waiting: "waiting", waitingforcustomer: "waiting", waitingforsupport: "waiting", pending: "waiting",
  done: "done", closed: "done", resolved: "done", complete: "done", completed: "done", cancelled: "done", wontdo: "done",
};
const PRIORITY_SYNONYMS = { blocker: "highest", critical: "highest", highest: "highest", major: "high", high: "high", medium: "medium", normal: "medium", minor: "low", low: "low", trivial: "lowest", lowest: "lowest" };
const CATEGORY = { todo: "todo", "to do": "todo", "in progress": "in_progress", done: "done" };

const pick = (list, value, synonyms) => {
  const n = norm(value);
  const exact = list.find((x) => norm(x.name) === n || norm(x.key) === n);
  if (exact) return exact;
  const key = synonyms[n];
  return key ? list.find((x) => x.key === key) : null;
};

export const autoMap = (data, ctx) => {
  const distinct = (f) => [...new Set(data.issues.map(f).filter(Boolean))];
  const types = {};
  for (const t of distinct((i) => i.type)) types[t] = String((pick(ctx.issueTypes, t, TYPE_SYNONYMS) || ctx.typeByKey.get("task") || {})._id || "");
  const statuses = {};
  for (const s of distinct((i) => i.status)) {
    const cat = CATEGORY[String(data.issues.find((i) => i.status === s)?.statusCategory || "").toLowerCase()];
    statuses[s] = String((pick(ctx.statuses, s, STATUS_SYNONYMS) || ctx.statuses.find((x) => x.category === cat) || {})._id || "");
  }
  const priorities = {};
  for (const p of distinct((i) => i.priority)) priorities[p] = String((pick(ctx.priorities, p, PRIORITY_SYNONYMS) || ctx.priorities.find((x) => x.isDefault) || {})._id || "");
  const people = {};
  const names = new Set([...data.issues.flatMap((i) => [i.assignee?.name, i.reporter?.name]), ...data.issues.flatMap((i) => i.comments.map((c) => data.accounts[c.authorId]))].filter(Boolean));
  for (const n of names) {
    const p = ctx.people.find((x) => norm(x.name) === norm(n) || (x.email && norm(x.email) === norm(n)));
    people[n] = p ? String(p.id) : "";
  }
  return { types, statuses, priorities, people };
};

// ---------------------------------------------------------------------------
// Downloadable template + column guide (shown in the wizard)

export const TEMPLATE_COLUMNS = [
  { name: "Issue key", required: true, example: "PRJ-12", note: "Unique id per row. The part before the dash is the project key; the number is kept when it is free." },
  { name: "Summary", required: true, example: "Signup form with OTP", note: "Title of the issue (max 500 characters)." },
  { name: "Issue Type", example: "Epic / Story / Task / Bug / Sub-task", note: "Mapped to your issue types in the next step. Empty = Task." },
  { name: "Status", example: "To Do / In Progress / Done", note: "Any names — you map them to your workflow statuses." },
  { name: "Priority", example: "Highest / High / Medium / Low", note: "Empty = workspace default." },
  { name: "Assignee", example: "rahul@company.com", note: "Name or email of a team member. Unknown people are left unassigned." },
  { name: "Reporter", example: "Mohan Sahu", note: "Name or email. Empty = you." },
  { name: "Parent key", example: "PRJ-2", note: "Issue key of the parent (epic for a story, story/task/bug for a sub-task). Sub-tasks need one." },
  { name: "Project key", example: "PRJ", note: "Optional. Several projects can be imported from one file." },
  { name: "Project name", example: "Customer Portal", note: "Optional. Used when a new project is created." },
  { name: "Description", example: "Steps to reproduce…", note: "Plain text. *bold*, _italic_ and lines starting with \"* \" (bullets) are formatted." },
  { name: "Labels", example: "frontend", note: "Repeat the column for more labels." },
  { name: "Story Points", example: "5", note: "Number." },
  { name: "Start date / Due date", example: "2026-10-15", note: "YYYY-MM-DD (time optional: 2026-10-15 14:30)." },
  { name: "Created / Updated / Resolved", example: "2026-09-01 10:00", note: "Original dates are kept on the issue." },
  { name: "Comment", example: "2026-09-02 11:00;Rahul Jain;Looks good", note: "date;author;text — repeat the column once per comment." },
];

const TEMPLATE_HEADER = ["Issue key", "Summary", "Issue Type", "Status", "Priority", "Assignee", "Reporter", "Parent key", "Project key", "Project name", "Description", "Labels", "Labels", "Story Points", "Start date", "Due date", "Created", "Updated", "Resolved", "Comment", "Comment"];
const TEMPLATE_ROWS = [
  ["PRJ-1", "Customer onboarding revamp", "Epic", "In Progress", "High", "", "", "", "PRJ", "Customer Portal", "Make signup and first login faster.", "onboarding", "", "", "2026-09-01", "2026-11-30", "2026-08-25 10:00", "2026-09-20 16:00", "", "", ""],
  ["PRJ-2", "Signup form with OTP", "Story", "In Review", "High", "rahul@company.com", "Mohan Sahu", "PRJ-1", "PRJ", "Customer Portal", "As a new customer I can sign up with my phone number.\n\n*Acceptance criteria*\n* OTP arrives within 30 seconds\n* Wrong OTP shows an error", "onboarding", "frontend", "5", "2026-09-05", "2026-10-10", "2026-08-28 11:30", "2026-09-25 18:00", "", "2026-09-10 12:00;Mohan Sahu;Please add a resend button", "2026-09-11 09:15;rahul@company.com;Added, ready for review"],
  ["PRJ-3", "Build OTP API", "Sub-task", "Done", "Medium", "rahul@company.com", "Mohan Sahu", "PRJ-2", "PRJ", "Customer Portal", "", "backend", "", "2", "", "", "2026-08-29 10:00", "2026-09-08 17:00", "2026-09-08 17:00", "", ""],
  ["PRJ-4", "Design form UI", "Sub-task", "To Do", "Medium", "", "Mohan Sahu", "PRJ-2", "PRJ", "Customer Portal", "", "design", "", "1", "", "2026-10-05", "2026-08-29 10:05", "", "", "", ""],
  ["PRJ-5", "Login fails on Safari", "Bug", "In Progress", "Highest", "Mohan Sahu", "rahul@company.com", "", "PRJ", "Customer Portal", "Steps:\n* Open the portal in Safari 17\n* Click Login\n\nThe page reloads instead of signing in.", "bug", "", "", "", "2026-10-03", "2026-09-28 09:00", "2026-09-29 14:00", "", "2026-09-28 10:00;rahul@company.com;Reproduced on macOS too", ""],
  ["PRJ-6", "Prepare release notes", "Task", "To Do", "Low", "", "", "PRJ-1", "PRJ", "Customer Portal", "", "release", "", "", "", "2026-11-25", "2026-09-30 15:00", "", "", "", ""],
];

const cell = (v) => (/[",\n;]/.test(v) ? `"${String(v).replace(/"/g, '""')}"` : v);
export const templateCsv = () => [TEMPLATE_HEADER, ...TEMPLATE_ROWS].map((r) => r.map(cell).join(",")).join("\r\n");
